import React, { useState, useEffect } from 'react';
import { Network, MapPin, AlertTriangle, Play, Pause } from 'lucide-react';

const MODES_SEQUENCE: Array<'raw' | 'clustered' | 'geo'> = ['raw', 'clustered', 'geo'];

export const NetworkTopologyVisualizer: React.FC = () => {
  const [mode, setMode] = useState<'raw' | 'clustered' | 'geo'>('raw');
  const [isAutoPlaying, setIsAutoPlaying] = useState<boolean>(true);

  // Auto-flip between the three correlation modes
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setMode((current) => {
        const currentIndex = MODES_SEQUENCE.indexOf(current);
        const nextIndex = (currentIndex + 1) % MODES_SEQUENCE.length;
        return MODES_SEQUENCE[nextIndex];
      });
    }, 4500);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handleModeSelect = (newMode: 'raw' | 'clustered' | 'geo') => {
    setMode(newMode);
    setIsAutoPlaying(false);
  };

  return (
    <div className="flat-card topo-minimal">
      <div className="topo-head-row">
        <div>
          <div className="pill-tag mb-2">
            <span className="status-dot blue" />
            <span>EVENT CORRELATION ENGINE</span>
          </div>
          <h3>From 200+ Alarms to 1 Root Incident Cluster</h3>
          <p className="subtext">
            Compare un-correlated alert storms against MUSE Telemetry's topological and geographic dependency clustering.
          </p>
        </div>

        <div className="toggle-box">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className={`t-btn ${isAutoPlaying ? 'active-auto' : ''}`}
            title={isAutoPlaying ? 'Pause automatic mode rotation' : 'Start automatic mode rotation'}
          >
            {isAutoPlaying ? <Pause size={12} className="btn-icon" /> : <Play size={12} className="btn-icon" />}
            <span>{isAutoPlaying ? 'Auto Tour' : 'Paused'}</span>
          </button>

          <button
            onClick={() => handleModeSelect('raw')}
            className={`t-btn ${mode === 'raw' ? 'active-raw' : ''}`}
            title="View 200+ un-correlated cascading alarms"
          >
            <AlertTriangle size={13} className="btn-icon red-icon" />
            <span>Raw Alarms (200+)</span>
          </button>

          <button
            onClick={() => handleModeSelect('clustered')}
            className={`t-btn ${mode === 'clustered' ? 'active-clustered' : ''}`}
            title="View topological root-cause correlation"
          >
            <Network size={13} className="btn-icon green-icon" />
            <span>Topology Cluster (1)</span>
          </button>

          <button
            onClick={() => handleModeSelect('geo')}
            className={`t-btn ${mode === 'geo' ? 'active-geo' : ''}`}
            title="View geographic map correlation & subscriber perimeter"
          >
            <MapPin size={13} className="btn-icon blue-icon" />
            <span>Geographic Map Cluster</span>
          </button>
        </div>
      </div>

      <div className="topo-canvas-frame">
        <div className="canvas-header-banner">
          {mode === 'raw' && (
            <div className="banner-line">
              <span className="status-dot red" />
              <span><strong>248 Isolated CPE Alarms Firing:</strong> High alert storm noise across multiple vendor dashboards. MTTR: ~3.5 Hours.</span>
            </div>
          )}
          {mode === 'clustered' && (
            <div className="banner-line">
              <span className="status-dot green" />
              <span><strong>1 Root Incident Cluster: Fiber Feeder Cut (Segment North 04)</strong>, 247 downstream CPE alarms suppressed into 1 ticket.</span>
            </div>
          )}
          {mode === 'geo' && (
            <div className="banner-line">
              <span className="status-dot blue" />
              <span><strong>1 Geolocation Incident Perimeter: North Highway 14 Corridor</strong> (Lat 41.88°N, Lon 87.63°W) — 247 subscribers pinpointed to a single 1.2-mile trench.</span>
            </div>
          )}
        </div>

        {/* SVG Canvas (Dual Mode: Tree Topology vs Geographic Map) */}
        {mode !== 'geo' ? (
          <svg className="minimal-svg" viewBox="0 0 800 300" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="min-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#141414" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="800" height="300" fill="url(#min-grid)" />

            {/* Main Distribution Links */}
            {/* North Backbone Link (Root Cut in Clustered Mode / Alarm Link in Raw Mode) */}
            <line 
              x1="400" y1="50" x2="200" y2="150" 
              stroke="#ef4444" 
              strokeWidth={mode === 'clustered' ? 2 : 1.5} 
            />
            {/* South Backbone Link (Healthy) */}
            <line x1="400" y1="50" x2="600" y2="150" stroke="#10b981" strokeWidth="1.5" />

            {/* North Tier 2 Hub Distribution Lines */}
            <line x1="200" y1="150" x2="120" y2="200" stroke={mode === 'raw' ? '#ef4444' : '#1e3a8a'} strokeWidth="1" />
            <line x1="200" y1="150" x2="200" y2="200" stroke={mode === 'raw' ? '#ef4444' : '#1e3a8a'} strokeWidth="1" />
            <line x1="200" y1="150" x2="280" y2="200" stroke={mode === 'raw' ? '#ef4444' : '#1e3a8a'} strokeWidth="1" />

            {/* North Tier 3 Subscriber CPE Drops */}
            {/* Hub N1 (120, 200) drops */}
            <line x1="120" y1="200" x2="60" y2="245" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="120" y1="200" x2="80" y2="268" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="120" y1="200" x2="105" y2="235" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="120" y1="200" x2="115" y2="270" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="120" y1="200" x2="140" y2="250" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />

            {/* Hub N2 (200, 200) drops */}
            <line x1="200" y1="200" x2="165" y2="240" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="200" y1="200" x2="185" y2="268" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="200" y1="200" x2="205" y2="242" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="200" y1="200" x2="220" y2="268" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="200" y1="200" x2="240" y2="245" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />

            {/* Hub N3 (280, 200) drops */}
            <line x1="280" y1="200" x2="260" y2="270" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="280" y1="200" x2="280" y2="240" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="280" y1="200" x2="305" y2="265" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="280" y1="200" x2="325" y2="238" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />
            <line x1="280" y1="200" x2="345" y2="260" stroke={mode === 'raw' ? '#ef4444' : '#172554'} strokeWidth="0.75" />

            {/* South Tier 2 Hub Distribution Lines */}
            <line x1="600" y1="150" x2="520" y2="200" stroke="#059669" strokeWidth="1" />
            <line x1="600" y1="150" x2="600" y2="200" stroke="#059669" strokeWidth="1" />
            <line x1="600" y1="150" x2="680" y2="200" stroke="#059669" strokeWidth="1" />

            {/* South Tier 3 Subscriber CPE Drops */}
            {/* Hub S1 (520, 200) drops */}
            <line x1="520" y1="200" x2="460" y2="245" stroke="#047857" strokeWidth="0.75" />
            <line x1="520" y1="200" x2="480" y2="268" stroke="#047857" strokeWidth="0.75" />
            <line x1="520" y1="200" x2="505" y2="235" stroke="#047857" strokeWidth="0.75" />
            <line x1="520" y1="200" x2="515" y2="270" stroke="#047857" strokeWidth="0.75" />
            <line x1="520" y1="200" x2="540" y2="250" stroke="#047857" strokeWidth="0.75" />

            {/* Hub S2 (600, 200) drops */}
            <line x1="600" y1="200" x2="565" y2="240" stroke="#047857" strokeWidth="0.75" />
            <line x1="600" y1="200" x2="585" y2="268" stroke="#047857" strokeWidth="0.75" />
            <line x1="600" y1="200" x2="605" y2="242" stroke="#047857" strokeWidth="0.75" />
            <line x1="600" y1="200" x2="620" y2="268" stroke="#047857" strokeWidth="0.75" />
            <line x1="600" y1="200" x2="640" y2="245" stroke="#047857" strokeWidth="0.75" />

            {/* Hub S3 (680, 200) drops */}
            <line x1="680" y1="200" x2="660" y2="270" stroke="#047857" strokeWidth="0.75" />
            <line x1="680" y1="200" x2="680" y2="240" stroke="#047857" strokeWidth="0.75" />
            <line x1="680" y1="200" x2="705" y2="265" stroke="#047857" strokeWidth="0.75" />
            <line x1="680" y1="200" x2="725" y2="238" stroke="#047857" strokeWidth="0.75" />
            <line x1="680" y1="200" x2="745" y2="260" stroke="#047857" strokeWidth="0.75" />

            {/* FIBER CUT HIGHLIGHT BOX (Only in Clustered Mode) */}
            {mode === 'clustered' && (
              <g>
                <circle cx="300" cy="100" r="26" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="3 3" fill="none" />
                <circle cx="300" cy="100" r="4" fill="#ef4444" />
                <rect x="220" y="48" width="160" height="18" rx="3" fill="#140505" stroke="#ef4444" strokeWidth="1" />
                <text x="300" y="60.5" fill="#fca5a5" fontSize="8" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">ROOT CAUSE: FIBER CUT</text>
              </g>
            )}

            {/* Core & Aggregation Parent Nodes */}
            <circle cx="400" cy="50" r="9" fill="#050505" stroke="#ffffff" strokeWidth="2" />
            <text x="400" y="32" fill="#ffffff" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">CORE-ASR</text>

            <circle cx="200" cy="150" r="9" fill="#050505" stroke={mode === 'raw' ? '#ef4444' : '#3b82f6'} strokeWidth="2" />
            <text x="200" y="134" fill={mode === 'raw' ? '#f87171' : '#93c5fd'} fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">AGG-NORTH</text>

            <circle cx="600" cy="150" r="9" fill="#050505" stroke="#10b981" strokeWidth="2" />
            <text x="600" y="134" fill="#6ee7b7" fontSize="9.5" fontFamily="var(--font-mono)" fontWeight="600" textAnchor="middle">AGG-SOUTH</text>

            {/* North Tier 2 Hub Nodes */}
            {[120, 200, 280].map((hx, idx) => (
              <circle 
                key={`nhub-${idx}`} 
                cx={hx} 
                cy="200" 
                r="5" 
                fill="#050505" 
                stroke={mode === 'raw' ? '#ef4444' : '#3b82f6'} 
                strokeWidth="1.5" 
              />
            ))}

            {/* South Tier 2 Hub Nodes */}
            {[520, 600, 680].map((hx, idx) => (
              <circle 
                key={`shub-${idx}`} 
                cx={hx} 
                cy="200" 
                r="5" 
                fill="#050505" 
                stroke="#10b981" 
                strokeWidth="1.5" 
              />
            ))}

            {/* North Downstream CPE Nodes (Flashing Red in Raw Mode / Calm Blue Suppressed in Clustered Mode) */}
            {[
              { x: 60, y: 245, r: 6 },
              { x: 80, y: 268, r: 4 },
              { x: 105, y: 235, r: 5 },
              { x: 115, y: 270, r: 7 },
              { x: 140, y: 250, r: 4 },
              { x: 165, y: 240, r: 5 },
              { x: 185, y: 268, r: 6 },
              { x: 205, y: 242, r: 4 },
              { x: 220, y: 268, r: 5 },
              { x: 240, y: 245, r: 7 },
              { x: 260, y: 270, r: 4 },
              { x: 280, y: 240, r: 5 },
              { x: 305, y: 265, r: 6 },
              { x: 325, y: 238, r: 4 },
              { x: 345, y: 260, r: 8 }
            ].map((cpe, i) => (
              <g key={`ncpe-${i}`}>
                <circle 
                  cx={cpe.x} 
                  cy={cpe.y} 
                  r={cpe.r} 
                  fill="#050505" 
                  stroke={mode === 'raw' ? '#ef4444' : '#3b82f6'} 
                  strokeWidth={mode === 'raw' ? 1.5 : 1} 
                />
                <circle 
                  cx={cpe.x} 
                  cy={cpe.y} 
                  r={cpe.r > 5 ? 2.5 : 1.5} 
                  fill={mode === 'raw' ? '#ef4444' : '#3b82f6'} 
                />
              </g>
            ))}

            {/* South Downstream CPE Nodes (Healthy Operational Green) */}
            {[
              { x: 460, y: 245, r: 6 },
              { x: 480, y: 268, r: 4 },
              { x: 505, y: 235, r: 5 },
              { x: 515, y: 270, r: 7 },
              { x: 540, y: 250, r: 4 },
              { x: 565, y: 240, r: 5 },
              { x: 585, y: 268, r: 6 },
              { x: 605, y: 242, r: 4 },
              { x: 620, y: 268, r: 5 },
              { x: 640, y: 245, r: 7 },
              { x: 660, y: 270, r: 4 },
              { x: 680, y: 240, r: 5 },
              { x: 705, y: 265, r: 6 },
              { x: 725, y: 238, r: 4 },
              { x: 745, y: 260, r: 8 }
            ].map((cpe, i) => (
              <g key={`scpe-${i}`}>
                <circle cx={cpe.x} cy={cpe.y} r={cpe.r} fill="#050505" stroke="#10b981" strokeWidth="1.2" />
                <circle cx={cpe.x} cy={cpe.y} r={cpe.r > 5 ? 2.5 : 1.5} fill="#10b981" />
              </g>
            ))}
          </svg>
        ) : (
          /* ================= GEOGRAPHIC MAP CLUSTER VIEW ================= */
          <svg className="minimal-svg geo-svg" viewBox="0 0 800 300" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="geo-grid" width="50" height="50" patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#131722" strokeWidth="0.75"/>
              </pattern>
              <filter id="glow-geo-red" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#ef4444" floodOpacity="0.6" />
              </filter>
              <filter id="glow-radar" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#ef4444" floodOpacity="0.9" />
              </filter>
            </defs>

            {/* Dark GIS Terrain & Grid */}
            <rect width="800" height="300" fill="#06090e" />
            <rect width="800" height="300" fill="url(#geo-grid)" />

            {/* Geographical River / Waterway Contour */}
            <path 
              d="M 330 0 C 350 70, 310 130, 360 200 C 390 240, 370 270, 380 300" 
              stroke="#0f2847" 
              strokeWidth="18" 
              fill="none" 
              opacity="0.65" 
            />

            {/* Regional Road & Highway Network */}
            {/* Highway 14 (East-West Major Arterial) */}
            <path d="M 0 110 L 800 110" stroke="#1e293b" strokeWidth="4" />
            <path d="M 0 110 L 800 110" stroke="#334155" strokeWidth="1.5" strokeDasharray="6 6" />

            {/* Route 9 (North-South Corridor) */}
            <path d="M 230 0 L 230 300" stroke="#1e293b" strokeWidth="3" />
            <path d="M 580 0 L 580 300" stroke="#1e293b" strokeWidth="3" />

            {/* Secondary Avenue Streets */}
            <line x1="0" y1="50" x2="800" y2="50" stroke="#0f172a" strokeWidth="1.5" />
            <line x1="0" y1="210" x2="800" y2="210" stroke="#0f172a" strokeWidth="1.5" />
            <line x1="110" y1="0" x2="110" y2="300" stroke="#0f172a" strokeWidth="1.2" />
            <line x1="450" y1="0" x2="450" y2="300" stroke="#0f172a" strokeWidth="1.2" />
            <line x1="710" y1="0" x2="710" y2="300" stroke="#0f172a" strokeWidth="1.2" />

            {/* Sector / District Geolocation Boundaries */}
            <text x="140" y="30" fill="#64748b" fontSize="8" fontFamily="var(--font-mono)" letterSpacing="0.08em">NORTH FIBER SECTOR</text>
            <text x="590" y="30" fill="#64748b" fontSize="8" fontFamily="var(--font-mono)" letterSpacing="0.08em">EAST INDUSTRIAL PARK</text>
            <text x="470" y="285" fill="#64748b" fontSize="8" fontFamily="var(--font-mono)" letterSpacing="0.08em">SOUTH METRO RESIDENTIAL</text>
            <text x="40" y="285" fill="#64748b" fontSize="8" fontFamily="var(--font-mono)" letterSpacing="0.08em">WEST SUBURBAN POD</text>

            {/* Core Central Office POP */}
            <g transform="translate(380, 140)">
              <rect x="-16" y="-14" width="32" height="28" rx="4" fill="#0f172a" stroke="#ffffff" strokeWidth="1.5" />
              <circle cx="0" cy="0" r="3" fill="#38bdf8" />
              <text x="0" y="24" fill="#ffffff" fontSize="8.5" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">CORE POP-01</text>
            </g>

            {/* Fiber Feeder Arterial Trunk (Central Office to North Hub) */}
            <path d="M 380 140 L 230 110" stroke="#ef4444" strokeWidth="2.5" strokeDasharray="4 3" />
            {/* Fiber Feeder Arterial Trunk (Central Office to South Hub) */}
            <path d="M 380 140 L 580 210" stroke="#10b981" strokeWidth="2" />

            {/* ================= GEOGRAPHIC CLUSTERING PERIMETER ================= */}
            {/* Shaded Red Polygon / Geofence around Outage Subscribers */}
            <polygon 
              points="55,45 285,40 315,115 270,165 85,160 50,110" 
              fill="rgba(239, 68, 68, 0.12)" 
              stroke="#ef4444" 
              strokeWidth="1.75" 
              strokeDasharray="5 4" 
              filter="url(#glow-geo-red)"
            />

            {/* Perimeter Cluster Label */}
            <rect x="62" y="46" width="200" height="20" rx="3" fill="#180404" stroke="#ef4444" strokeWidth="1" />
            <text x="162" y="59.5" fill="#fca5a5" fontSize="7.5" fontFamily="var(--font-mono)" fontWeight="700" textAnchor="middle">
              CORRELATED INCIDENT PERIMETER (1.2 MI)
            </text>

            {/* Excavator Cut Radar Locus / Crosshair Target */}
            <g transform="translate(230, 110)">
              {/* Radar Wave Rings */}
              <circle cx="0" cy="0" r="28" stroke="#ef4444" strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
              <circle cx="0" cy="0" r="16" stroke="#ef4444" strokeWidth="1.2" opacity="0.8" />
              <circle cx="0" cy="0" r="5" fill="#ef4444" filter="url(#glow-radar)" />
              {/* Crosshairs */}
              <line x1="-12" y1="0" x2="12" y2="0" stroke="#ffffff" strokeWidth="1.5" />
              <line x1="0" y1="-12" x2="0" y2="12" stroke="#ffffff" strokeWidth="1.5" />
              {/* Locus Callout Tag */}
              <rect x="18" y="-19" width="160" height="26" rx="3" fill="#090d16" stroke="#ef4444" strokeWidth="1.5" />
              <text x="24" y="-7" fill="#f87171" fontSize="7.5" fontFamily="var(--font-mono)" fontWeight="700">ROOT TRENCH CUT LOCUS</text>
              <text x="24" y="3" fill="#cbd5e1" fontSize="7" fontFamily="var(--font-mono)">HWY 14 &amp; 4TH AVE (247 DROPS)</text>
            </g>

            {/* ================= SUBSCRIBER GEOGRAPHIC PINS ================= */}
            {/* Impacted Subscriber Drops inside the Red Incident Perimeter */}
            {[
              { x: 80, y: 75, r: 4.5 },
              { x: 105, y: 95, r: 5 },
              { x: 130, y: 80, r: 6.5 },
              { x: 155, y: 135, r: 4 },
              { x: 175, y: 90, r: 5.5 },
              { x: 190, y: 145, r: 7 },
              { x: 215, y: 70, r: 4.5 },
              { x: 245, y: 140, r: 5 },
              { x: 265, y: 85, r: 6 },
              { x: 285, y: 130, r: 4 },
              { x: 115, y: 140, r: 5 },
              { x: 140, y: 110, r: 6 },
              { x: 180, y: 120, r: 4.5 },
              { x: 250, y: 65, r: 5 }
            ].map((pin, i) => (
              <g key={`geo-impact-${i}`}>
                <circle cx={pin.x} cy={pin.y} r={pin.r} fill="#180404" stroke="#ef4444" strokeWidth="1.2" />
                <circle cx={pin.x} cy={pin.y} r={pin.r > 5 ? 2.5 : 1.5} fill="#ef4444" />
              </g>
            ))}

            {/* Operational / Healthy Subscribers across South, East, and West */}
            {[
              { x: 60, y: 230, r: 5 },
              { x: 95, y: 250, r: 4 },
              { x: 150, y: 265, r: 6 },
              { x: 200, y: 240, r: 5 },
              { x: 450, y: 80, r: 5 },
              { x: 480, y: 140, r: 4 },
              { x: 510, y: 65, r: 6 },
              { x: 535, y: 125, r: 4.5 },
              { x: 620, y: 80, r: 5 },
              { x: 650, y: 140, r: 6.5 },
              { x: 690, y: 95, r: 4 },
              { x: 740, y: 130, r: 5.5 },
              { x: 440, y: 235, r: 5 },
              { x: 490, y: 265, r: 6.5 },
              { x: 540, y: 240, r: 4.5 },
              { x: 580, y: 270, r: 5 },
              { x: 630, y: 235, r: 6 },
              { x: 670, y: 265, r: 4 },
              { x: 720, y: 240, r: 5.5 }
            ].map((pin, i) => (
              <g key={`geo-healthy-${i}`}>
                <circle cx={pin.x} cy={pin.y} r={pin.r} fill="#042018" stroke="#10b981" strokeWidth="1.2" />
                <circle cx={pin.x} cy={pin.y} r={pin.r > 5 ? 2.5 : 1.5} fill="#10b981" />
              </g>
            ))}

            {/* Subtle GIS Coordinates Overlay */}
            <text x="740" y="20" fill="#334155" fontSize="7.5" fontFamily="var(--font-mono)" textAnchor="end">GIS: 41.882°N, 87.629°W</text>
          </svg>
        )}

        <div className="canvas-footer-legend">
          {mode !== 'geo' ? (
            <>
              <div className="l-item"><span className="status-dot green" /> Operational Path</div>
              <div className="l-item"><span className="status-dot red" /> Outage / Cut Line</div>
              <div className="l-item"><span className="status-dot blue" /> Suppressed Downstream Node</div>
            </>
          ) : (
            <>
              <div className="l-item"><span className="status-dot green" /> Operational Subscribers</div>
              <div className="l-item"><span className="status-dot red" /> Correlated Outage Cluster</div>
              <div className="l-item"><span className="status-dot geo-border-dot" /> Geographic Correlation Perimeter</div>
              <div className="l-item"><span className="status-dot white-dot" /> Central Office POP</div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .topo-minimal {
          margin: 2rem 0;
        }

        .topo-head-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1.5rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
        }

        .topo-head-row h3 {
          font-size: 1.4rem;
          margin-bottom: 0.3rem;
        }

        .toggle-box {
          display: flex;
          gap: 0.4rem;
          background: #050505;
          padding: 0.25rem;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
          flex-wrap: wrap;
        }

        .t-btn {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          background: transparent;
          border: none;
          color: var(--text-muted);
          font-size: 0.78rem;
          padding: 0.38rem 0.75rem;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .t-btn:hover {
          color: #ffffff;
        }

        .t-btn.active-auto {
          background: #1e293b;
          color: #38bdf8;
          font-weight: 600;
          border: 1px solid rgba(56, 189, 248, 0.3);
        }

        .t-btn.active-raw {
          background: #181818;
          color: #ffffff;
          font-weight: 600;
        }

        .t-btn.active-clustered {
          background: #ffffff;
          color: #000000;
          font-weight: 600;
        }

        .t-btn.active-geo {
          background: #0284c7;
          color: #ffffff;
          font-weight: 600;
        }

        .btn-icon {
          flex-shrink: 0;
        }

        .red-icon {
          color: #ef4444;
        }

        .green-icon {
          color: #10b981;
        }

        .blue-icon {
          color: #38bdf8;
        }

        .topo-canvas-frame {
          background: #050505;
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          overflow: hidden;
        }

        .canvas-header-banner {
          background: #0d0d0d;
          border-bottom: 1px solid var(--border-color);
          padding: 0.65rem 1rem;
        }

        .banner-line {
          display: flex;
          align-items: center;
          gap: 0.6rem;
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .banner-line strong {
          color: #ffffff;
        }

        .minimal-svg {
          width: 100%;
          height: auto;
          min-height: 250px;
          display: block;
        }

        .geo-svg {
          background: #06090e;
        }

        .canvas-footer-legend {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2rem;
          padding: 0.65rem;
          background: #000000;
          border-top: 1px solid var(--border-color);
          font-size: 0.75rem;
          color: var(--text-dim);
          flex-wrap: wrap;
          font-family: var(--font-mono);
        }

        .l-item {
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }

        .geo-border-dot {
          background: transparent;
          border: 1px dashed #ef4444;
          box-shadow: 0 0 4px rgba(239, 68, 68, 0.5);
        }

        .white-dot {
          background: #ffffff;
          box-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
        }
      `}</style>
    </div>
  );
};
